import React, { useEffect, useId, useRef, useState } from "react";
import { useProjects } from "../contexts/ProjectContext";
import { useTranslation } from "react-i18next";
import { Check, LoaderCircle, X } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from "./ui/sheet";

// Email validation regex
const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

const getPendingEmailResult = (committedEmails, pendingInput) => {
  const emailsToAdd = pendingInput
    .split(",")
    .map((email) => email.trim())
    .filter(Boolean);
  const invalidEmail = emailsToAdd.find((email) => !EMAIL_REGEX.test(email));

  if (invalidEmail) {
    return { isValid: false, invalidEmail, emails: committedEmails };
  }

  return {
    isValid: true,
    emails: [...new Set([...committedEmails, ...emailsToAdd])],
  };
};

const CreateProjectForm = ({
  open,
  onClose,
  onCreateProject = null,
  returnFocusRef = null,
}) => {
  const { t, i18n } = useTranslation();
  const { createProject: createProjectFromContext } = useProjects();
  const createProject = onCreateProject ?? createProjectFromContext;
  const fieldId = useId();
  const sheetNameInputRef = useRef(null);
  const returnFocusTargetRef = useRef(null);
  const submittingRef = useRef(false);
  const sessionRef = useRef(0);
  const [projectName, setProjectName] = useState("");
  const [projectDescription, setProjectDescription] = useState("");
  const [userEmails, setUserEmails] = useState([]);
  const [emailInput, setEmailInput] = useState("");
  const [emailError, setEmailError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [missingUsers, setMissingUsers] = useState([]);
  const previousOpenRef = useRef(open);

  useEffect(() => {
    if (previousOpenRef.current && !open) {
      sessionRef.current += 1;
      submittingRef.current = false;
      setIsSubmitting(false);
      setProjectName("");
      setProjectDescription("");
      setUserEmails([]);
      setEmailInput("");
      setEmailError("");
      setFormError("");
      setMissingUsers([]);
    }
    previousOpenRef.current = open;
  }, [open]);

  useEffect(
    () => () => {
      sessionRef.current += 1;
      submittingRef.current = false;
    },
    [],
  );

  const handleNameChange = (e) => {
    setProjectName(e.target.value);
    if (formError) setFormError("");
  };

  const handleDescriptionChange = (e) => {
    setProjectDescription(e.target.value);
  };

  const handleEmailInputChange = (e) => {
    setEmailInput(e.target.value);
    // Clear error when typing
    if (emailError) setEmailError("");
    // Clear missing users when typing new emails
    if (missingUsers.length > 0) setMissingUsers([]);
  };

  const addEmails = () => {
    if (!emailInput.trim()) return { isValid: true, emails: userEmails };

    const validation = getPendingEmailResult(userEmails, emailInput);

    if (!validation.isValid) {
      setEmailError(
        t("createProject.invalidEmailFormat", {
          email: validation.invalidEmail,
        }),
      );
      return validation;
    }

    setUserEmails(validation.emails);
    setEmailInput("");
    setEmailError("");
    setMissingUsers([]); // Clear missing users when new emails are added
    return validation;
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addEmails();
    }
  };

  const handleEmailBlur = () => {
    addEmails();
  };

  const handleDeleteEmail = (emailToDelete) => {
    setUserEmails(userEmails.filter((email) => email !== emailToDelete));

    // If this email was in the missing users list, remove it from there too
    if (missingUsers.includes(emailToDelete)) {
      setMissingUsers(missingUsers.filter((email) => email !== emailToDelete));
    }
  };

  const handleSubmit = async (event) => {
    event?.preventDefault?.();
    if (submittingRef.current) return;

    // Validate form
    if (!projectName.trim()) {
      setFormError(t("createProject.projectNameRequired"));
      return;
    }

    const pendingEmails = getPendingEmailResult(userEmails, emailInput);
    if (!pendingEmails.isValid) {
      setEmailError(
        t("createProject.invalidEmailFormat", {
          email: pendingEmails.invalidEmail,
        }),
      );
      return;
    }

    if (emailInput.trim()) {
      setUserEmails(pendingEmails.emails);
      setEmailInput("");
      setEmailError("");
      setMissingUsers([]);
    }

    const submissionSession = sessionRef.current;
    submittingRef.current = true;

    try {
      setIsSubmitting(true);
      setFormError("");
      setMissingUsers([]);

      // Create project via API
      await createProject({
        name: projectName.trim(),
        description: projectDescription.trim(),
        teamMembers: pendingEmails.emails,
      });

      if (sessionRef.current === submissionSession) {
        closeAndReset();
      }
    } catch (error) {
      if (sessionRef.current !== submissionSession) return;

      // Check if this is a missing users error
      if (error.missingUsers && Array.isArray(error.missingUsers)) {
        setMissingUsers(error.missingUsers);
        setFormError(
          t("createProject.usersNotFound", { message: error.message }),
        );
      } else {
        setFormError(error.message || t("createProject.createError"));
      }
    } finally {
      if (sessionRef.current === submissionSession) {
        submittingRef.current = false;
        setIsSubmitting(false);
      }
    }
  };

  const resetForm = () => {
    setProjectName("");
    setProjectDescription("");
    setUserEmails([]);
    setEmailInput("");
    setEmailError("");
    setFormError("");
    setMissingUsers([]);
  };

  const closeAndReset = () => {
    sessionRef.current += 1;
    submittingRef.current = false;
    setIsSubmitting(false);
    resetForm();
    onClose();
  };

  const handleSheetOpenChange = (nextOpen) => {
    if (!nextOpen && !submittingRef.current) {
      closeAndReset();
    }
  };

  const preventPendingDismissal = (event) => {
    if (submittingRef.current) event.preventDefault();
  };

  const isRTL =
    (typeof document !== "undefined" &&
      document.documentElement.dir === "rtl") ||
    i18n.dir() === "rtl";
  const nameId = `${fieldId}-name`;
  const descriptionId = `${fieldId}-description`;
  const emailId = `${fieldId}-emails`;
  const emailErrorId = `${fieldId}-email-error`;
  const formErrorId = `${fieldId}-form-error`;

  return (
    <Sheet open={open} onOpenChange={handleSheetOpenChange}>
        <SheetContent
          side={isRTL ? "left" : "right"}
          dir={isRTL ? "rtl" : "ltr"}
          showCloseButton={false}
          overlayClassName="bg-transparent"
          className="top-[60px] h-[calc(100dvh-60px)] w-[min(500px,100vw)] max-w-full gap-0 border-border bg-card p-0 shadow-lg"
          onOpenAutoFocus={(event) => {
            const configuredTarget = returnFocusRef?.current;
            returnFocusTargetRef.current =
              configuredTarget instanceof HTMLElement && configuredTarget.isConnected
                ? configuredTarget
                : document.activeElement instanceof HTMLElement
                  ? document.activeElement
                  : null;
            event.preventDefault();
            sheetNameInputRef.current?.focus({ preventScroll: true });
          }}
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            const target = returnFocusTargetRef.current;
            if (
              target instanceof HTMLElement &&
              target.isConnected &&
              !("disabled" in target && target.disabled)
            ) {
              target.focus({ preventScroll: true });
            }
            returnFocusTargetRef.current = null;
          }}
          onEscapeKeyDown={preventPendingDismissal}
          onPointerDownOutside={preventPendingDismissal}
          onInteractOutside={preventPendingDismissal}
          data-testid="create-project-sheet"
        >
          <form onSubmit={handleSubmit} className="flex h-full min-h-0 flex-col">
            <header className="flex h-14 shrink-0 items-center gap-2 px-4 pt-2">
              <SheetTitle className="min-w-0 flex-1 text-xs font-normal text-muted-foreground">
                {t("createProject.title")}
              </SheetTitle>
              <SheetDescription className="sr-only">
                {t("createProject.sheetDescription")}
              </SheetDescription>
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                aria-label={t("common.close")}
                onClick={closeAndReset}
                disabled={isSubmitting}
                className="size-6 text-muted-foreground"
              >
                <X className="size-4" aria-hidden="true" />
              </Button>
            </header>

            <div className="min-h-0 flex-1 overflow-y-auto px-3 pb-4">
              {formError && (
                <p
                  id={formErrorId}
                  role="alert"
                  className="mb-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
                >
                  {formError}
                </p>
              )}

              <section aria-labelledby={`${fieldId}-project-info`}>
                <h2
                  id={`${fieldId}-project-info`}
                  className="flex h-[45px] items-center py-3 text-base font-medium text-foreground"
                >
                  {t("createProject.projectInfo")}
                </h2>
                <div className="space-y-4">
                  <div className="space-y-1">
                    <label
                      htmlFor={nameId}
                      className="block text-xs font-medium text-muted-foreground"
                    >
                      {t("createProject.nameLabel")}
                    </label>
                    <Input
                      ref={sheetNameInputRef}
                      id={nameId}
                      required
                      value={projectName}
                      onChange={handleNameChange}
                      placeholder={t("createProject.sheetNamePlaceholder")}
                      disabled={isSubmitting}
                      aria-invalid={Boolean(formError && !projectName.trim())}
                      aria-describedby={formError ? formErrorId : undefined}
                      className="rounded-lg border-border bg-input"
                    />
                  </div>

                  <div className="space-y-1">
                    <label
                      htmlFor={descriptionId}
                      className="block text-xs font-medium text-muted-foreground"
                    >
                      {t("createProject.descriptionLabel")}
                    </label>
                    <textarea
                      id={descriptionId}
                      value={projectDescription}
                      onChange={handleDescriptionChange}
                      placeholder={t("createProject.sheetDescriptionPlaceholder")}
                      disabled={isSubmitting}
                      className="h-[109px] w-full resize-none rounded-lg border border-border bg-input px-3 py-2 text-sm text-foreground shadow-xs outline-none transition-[color,box-shadow] placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50"
                    />
                  </div>
                </div>
              </section>

              <section aria-labelledby={`${fieldId}-members`} className="mt-4">
                <h2
                  id={`${fieldId}-members`}
                  className="flex h-[45px] items-center py-3 text-base font-medium text-foreground"
                >
                  {t("projects.members")}
                </h2>
                <div className="flex items-end gap-2 py-2">
                  <div className="min-w-0 flex-1 space-y-1">
                    <label
                      htmlFor={emailId}
                      className="block text-xs font-medium text-secondary-foreground"
                    >
                      {t("createProject.emailsLabel")}
                    </label>
                    <Input
                      id={emailId}
                      value={emailInput}
                      onChange={handleEmailInputChange}
                      onKeyDown={handleKeyDown}
                      onBlur={handleEmailBlur}
                      placeholder={t("createProject.sheetEmailsPlaceholder")}
                      disabled={isSubmitting}
                      aria-invalid={Boolean(emailError)}
                      aria-describedby={emailError ? emailErrorId : undefined}
                      className="h-10 rounded-lg border-border bg-input"
                    />
                  </div>
                  <Button
                    type="button"
                    size="icon"
                    aria-label={t("createProject.addEmails")}
                    onClick={addEmails}
                    disabled={isSubmitting || !emailInput.trim()}
                    className="size-9 rounded-lg"
                  >
                    <Check className="size-4" aria-hidden="true" />
                  </Button>
                </div>
                {emailError && (
                  <p id={emailErrorId} role="alert" className="text-xs text-destructive">
                    {emailError}
                  </p>
                )}

                {userEmails.length > 0 && (
                  <div
                    className="mt-2 flex flex-wrap gap-2"
                    aria-label={t("createProject.invitedMembers")}
                  >
                    {userEmails.map((email) => {
                      const missing = missingUsers.includes(email);
                      return (
                        <span
                          key={email}
                          className={`inline-flex h-7 max-w-full items-center gap-1 rounded-md border px-2 text-xs ${
                            missing
                              ? "border-destructive bg-destructive text-destructive-foreground"
                              : "border-border bg-muted text-foreground"
                          }`}
                        >
                          <span className="truncate">{email}</span>
                          <button
                            type="button"
                            disabled={isSubmitting}
                            onClick={() => handleDeleteEmail(email)}
                            aria-label={t("createProject.removeEmail", { email })}
                            className="rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            <X className="size-3" aria-hidden="true" />
                          </button>
                        </span>
                      );
                    })}
                  </div>
                )}
              </section>
            </div>

            <footer className="flex h-[68px] shrink-0 items-center justify-end gap-2 border-t border-border px-4 py-4">
              <Button
                type="button"
                variant="outline"
                onClick={closeAndReset}
                disabled={isSubmitting}
              >
                {t("common.cancel")}
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting || !projectName.trim()}
              >
                {isSubmitting && (
                  <LoaderCircle
                    className="size-4 animate-spin motion-reduce:animate-none"
                    aria-hidden="true"
                  />
                )}
                {isSubmitting ? t("common.creating") : t("projects.createProject")}
              </Button>
            </footer>
          </form>
      </SheetContent>
    </Sheet>
  );
};

export default CreateProjectForm;
